import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { api, homeForRole } from "@/lib/api";
import { BrandMark } from "@/components/BrandMark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const GoogleButton = () => {
  const [busy, setBusy] = useState(false);
  const startGoogle = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/auth/google/config");
      const redirectUri = window.location.origin + "/auth/google";
      const state = Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem("g_oauth_state", state);
      const params = new URLSearchParams({
        client_id: data.clientId,
        redirect_uri: redirectUri,
        response_type: "code",
        scope: "openid email profile",
        state,
        access_type: "online",
        prompt: "select_account",
      });
      window.location.href = `https://accounts.google.com/o/oauth2/v2/auth?${params.toString()}`;
    } catch {
      setBusy(false);
    }
  };
  return (
    <Button type="button" variant="outline" onClick={startGoogle} disabled={busy} data-testid="google-login-btn" className="w-full rounded-xl py-6 text-base">
      {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : (
      <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.26 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38z" />
      </svg>
      )}
      Continue with Google
    </Button>
  );
};

const Shell = ({ title, subtitle, children }) => (
  <div className="mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-4 py-10">
    <Link to="/" className="mb-8 flex items-center gap-2">
      <BrandMark className="h-9" />
      <span className="font-heading text-lg font-bold">Z2Infinity</span>
    </Link>
    <h1 className="font-heading text-3xl font-bold tracking-tight">{title}</h1>
    <p className="mt-2 text-muted-foreground">{subtitle}</p>
    <div className="mt-8">{children}</div>
  </div>
);

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const deactivated = new URLSearchParams(location.search).get("deactivated") === "1";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      const dest = location.state?.from && user.role === "STUDENT" ? location.state.from : homeForRole(user.role);
      navigate(user.role === "STUDENT" && !user.onboardingCompleted ? "/onboarding" : dest, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Shell title="Welcome back" subtitle="Sign in to continue your JEE / NEET prep.">
      {deactivated && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300" data-testid="deactivated-banner">
          Your account has been deactivated. Please contact support if you think this is a mistake.
        </div>
      )}
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div className="space-y-2">
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" data-testid="login-email" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-xl" placeholder="you@example.com" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input id="password" type="password" data-testid="login-password" value={password} onChange={(e) => setPassword(e.target.value)} required className="rounded-xl" placeholder="••••••••" />
        </div>
        {error && <p className="text-sm text-red-500" data-testid="login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full rounded-xl py-6 text-base">
          {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Sign in
        </Button>
      </form>
      <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-widest text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-muted-foreground">
        New here?{" "}
        <Link to="/register" className="font-medium text-primary hover:underline" data-testid="go-register">
          Create an account
        </Link>
      </p>
    </Shell>
  );
}

export { Shell, GoogleButton };
